import { AxiosResponse } from "axios";
import axiosInstance from "@/axiosConfig";
import { buildUrlWithPagination } from "@/shared/utils/api";
import { ACTIVE_JOBS_SEED_LIMIT } from "../configs/constants";
import { JOBS_ENDPOINTS } from "../configs/endpoints";
import type {
  CreateJobParams,
  CreateJobToCreateVoucherParams,
  GetJobsServiceRequestParams,
  GetJobsServiceResponse,
  Job,
  JobServiceResponse,
} from "../types/jobs.types";

export const getJobs = async ({
  page = 1,
  limit = 20,
  sort = true,
  search = "",
}: GetJobsServiceRequestParams): Promise<AxiosResponse<GetJobsServiceResponse>> => {
  const queryParams = {
    s: sort ? "created_at_desc" : "",
    limit: limit.toString(),
    search,
  };

  const url = buildUrlWithPagination(JOBS_ENDPOINTS.GET_JOBS, page, queryParams);

  return axiosInstance.get<GetJobsServiceResponse>(url);
};

export const getInProgressJobs = async (): Promise<AxiosResponse<GetJobsServiceResponse>> => {
  const url = buildUrlWithPagination(JOBS_ENDPOINTS.GET_JOBS, 1, {
    "q[active_for_counter]": "true",
    "limit": ACTIVE_JOBS_SEED_LIMIT.toString(),
  });
  return axiosInstance.get<GetJobsServiceResponse>(url);
};

export const getJob = async (jobId: string): Promise<AxiosResponse<JobServiceResponse>> => {
  return axiosInstance.get<JobServiceResponse>(JOBS_ENDPOINTS.GET_JOB(jobId));
};

export const postJob = async (data: CreateJobParams): Promise<AxiosResponse<JobServiceResponse>> => {
  const body = {
    data: {
      attributes: {
        "resource": data.type,
        "job-piece": data.signedId,
        ...("params" in data ? data.params : {}),
      },
    },
  };

  return axiosInstance.post<JobServiceResponse>(JOBS_ENDPOINTS.CREATE_JOB, body);
};

export const postJobToCreateVoucher = async (data: CreateJobToCreateVoucherParams): Promise<AxiosResponse<JobServiceResponse>> => {
  const body = {
    data: {
      attributes: {
        resource: data.type,
        ...("params" in data ? data.params : {}),
      },
    },
  };

  return axiosInstance.post<JobServiceResponse>(JOBS_ENDPOINTS.CREATE_VOUCHER_ORDER, body);
};

export const cancelJob = async (id: Job["uuid"]): Promise<AxiosResponse<unknown>> =>
  axiosInstance.delete<unknown>(JOBS_ENDPOINTS.DELETE_JOB(id));

// Async, reverse-progress cancellation of an employee import: the backend flags the job as
// cancelling and returns 202 with the job; the descending gauge then arrives via Pusher.
export const cancelEmployeeImport = async (id: Job["uuid"]): Promise<AxiosResponse<JobServiceResponse>> =>
  axiosInstance.post<JobServiceResponse>(JOBS_ENDPOINTS.CANCEL_JOB(id));
